<script setup>
import { inject, defineProps, defineEmits, ref, computed, onMounted, watch } from 'vue';

// Vue Components
import HeaderLogo from '../svg/HeaderLogo.vue';
import IconMenu from '../svg/IconMenu.vue';
import IconInstagram from '../svg/IconInstagram.vue';

// Functions
import popObjFromSiteMap from '../functions/popObjFromSiteMap.js';

const mq = inject(['mq']);

const props = defineProps({
    siteMap: {
        type: Object,
        default: {}
    },
    snsList: {
        type: Object,
        default: {}
    },
    information: {
        type: Object,
        default: {}
    },
    colors: {
        type: Object,
        default: {}
    },
    pageId: {
        type: String,
        default: ''
    },
});

const emit = defineEmits(['header-h']);

// SNSアイコンとSVGの対応付け
const snsIconsMap = {
    instagram: IconInstagram
}

// 要素の取得
const header = ref(null);

// リアクティブデータの定義
const windowH = ref(window.innerHeight); //ウィンドウの高さ
const headerH = ref(0); //ヘッダーの高さ
const isActive = ref(false); //グロナビ表示管理
const isFirstView = props.pageId === 'frontpage' ? ref(true) : ref(false); //トップページ　ファーストビュー管理

// グロナビ
const gnavList = computed(() => {
    const keys = ['story', 'stay', 'toka', 'facility', 'location'];
    return popObjFromSiteMap(keys, props.siteMap);
});

// グロナビ(サブ)
const gnavSubList = computed(() => {
    let keys = ['news', 'faq'];
    if (mq.mdPlus) keys.push('contact'); //PCの場合contactを追加

    return popObjFromSiteMap(keys, props.siteMap);
});

// メニューアイコンに渡すカラーコード
const menubarColor = computed(() => {
    return isFirstView.value ? '#fff' : props.colors.base;
});

// ヘッダーの高さを取得
const getHeaderH = () => {
    headerH.value = header.value.getBoundingClientRect().height;
    emit('header-h', headerH.value); //親コンポーネントに渡す
}

// グロナビ出現時にbodyを固定
const fixBody = (val) => {
    const body = document.body;
    const duration = 200; //グロナビ出現にかかる時間
    
    if (val) {
        const scroll = window.scrollY;
        setTimeout(() => {
            body.classList.add('is-locked');
            body.style.top = `${-scroll}px`;
        }, duration);
    } else {
        const scroll = -parseFloat(body.style.top);
        body.classList.remove('is-locked');
        body.style.top = '';
        window.scrollTo(0, scroll);
    }
}

// ファーストビューかどうか監視
const observeFirstView = () => {
    if (props.pageId !== 'frontpage') return; //トップページ以外なら終了

    // スクロール時に呼ばれる関数
    const doWhenScroll = () => {
        if (isActive.value) return; //グロナビ展開中なら終了

        const target = document.getElementById('mv');
        const targetPos = target.getBoundingClientRect().height;
        const scroll = window.scrollY;

        if (scroll > targetPos) {
            isFirstView.value = false;
        } else {
            isFirstView.value = true;
        }
    }
    doWhenScroll(); //初期描画
    window.addEventListener('scroll', doWhenScroll);
}

// ロード後に発火
const onLoaded = () => {
    window.addEventListener('load', getHeaderH);
    window.addEventListener('load', observeFirstView);
}

// マウント時に発火
onMounted(() => {
    onLoaded();
});

// メディアクエリを監視
watch(() => mq.current, () => {
    windowH.value = window.innerHeight;
    getHeaderH();
});

// グロナビ表示を監視
watch(() => isActive.value, (newVal) => {
    fixBody(newVal);
});
</script>

<template>
<header class="header" ref="header" :class="{'is-fv': isFirstView}">
    <p class="header__logo" v-if="!isFirstView">
        <a href="/"><HeaderLogo/></a>
    </p>
    <div class="header__gnav-area">
        <ul class="header__list" v-if="mq.xlPlus && !isFirstView">
            <li class="header__item" :class="{'is-current': pageId === key}" v-for="val, key in gnavList" :key="key">
                <a :href="val.url">{{ val.name }}</a>
            </li>
        </ul>
        <a class="btn is-header" :class="{'is-white': isFirstView}" :href="information.reservation.url" target="_blank">
            <span class="btn__en">{{ information.reservation.name }}</span>
            <span class="btn__ja" v-if="mq.mdPlus"> / {{ information.reservation.nameJa }}</span>
        </a>
        <a class="header__menubar" @click="isActive = true">
            <IconMenu :color="menubarColor"/>
        </a>
    </div>

    <!-- menu -->
    <div class="menu" :class="{'is-active': isActive}" :style="{'--end-height': `${windowH}px`}">
        <a class="menu__close" @click="isActive = false">
            <img src="/assets/img/common/icon_close.svg" alt="閉じる">
        </a>
        <div class="inner is-small">
            <div class="menu__area">
                <p class="menu__logo">
                    <a href="/">
                        <HeaderLogo v-if="!mq.mdPlus" color="#fff"/>
                        <img v-else src="/assets/img/common/logo.svg" alt="HOTEL FARO manazuru" width="139" height="150">
                    </a>
                </p>
                <nav class="menu__nav">
                    <ul class="menu__list">
                        <li class="menu__item" v-for="val, key in gnavList" :key="key">
                            <a :href="val.url">
                                <span class="menu__en">{{ val.name }}</span>
                                <span class="menu__ja">{{ val.nameJa }}</span>
                            </a>
                        </li>
                    </ul>
                    <div class="menu__col">
                        <ul class="menu__sub-list">
                            <li class="menu__sub-item" v-for="val, key in gnavSubList" :key="key">
                                <a :href="val.url">{{ val.nameJa }}</a>
                            </li>
                        </ul>
                        <ul class="menu__sns-list">
                            <li class="menu__sns-item" v-for="val, key in snsList" :key="key">
                                <a :href="val.url" target="_blank">
                                    <component :is="snsIconsMap[key]" color="#fff"/>
                                    {{ val.name }}
                                </a>
                            </li>
                        </ul>
                        <div class="menu__btn-area">
                            <address v-if="mq.mdPlus" class="menu__address">
                                {{ information.zipCode }}<br>
                                {{ information.address }}<br>
                                FAX {{ information.fax }}
                            </address>
                            <a v-if="!mq.mdPlus" class="btn is-menu is-transparent" :href="siteMap.contact.url">
                                <span class="btn__en">{{ siteMap.contact.name }}</span>
                                <span class="btn__ja">{{ siteMap.contact.nameJa }}</span>
                                <span class="arrow btn__arrow"></span>
                            </a>
                            <a :href="information.reservation.url" target="_blank" class="btn is-menu is-white menu__btn">
                                <span class="btn__en">{{ information.reservation.name }}</span>
                                <span class="btn__ja">{{ information.reservation.nameJa }}</span>
                                <span class="arrow btn__arrow"></span>
                            </a>
                        </div>
                    </div>
                </nav>
            </div>
        </div>
    </div><!-- ./menu -->
    
</header>
</template>